import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  CalculatorIcon,
  DeleteIcon,
  ErrorfilledIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { CalculationRowData } from "interfaces/Revenue";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  useDeleteFeeCalculationMutation,
  useGetFeeCalculationsQuery,
} from "store/features/Revenue/FeeCalculationApi";
import { paletteLightTheme } from "utils/ColorTheme";
import { moneyCommaSeparator } from "utils/MathFunction";
import { handleCapitalize } from "utils/common";
import { extraColumnsField, matchDate } from "utils/helpers";
import CalculationDetailsModal from "./CalculationDetailsModal";
import ProformaModal from "./ProformaModal";

interface Props {
  name?: string;
  portfolioId?: string;
  tableName?: string;
}

const FeeCalculation = (props: Props) => {
  const { name, portfolioId, tableName } = props;

  const navigate = useNavigate();

  const [deleteCalculationId, setDeleteCalculationId] = useState(null);
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [openProformaModal, setOpenProformaModal] = useState(false);
  const [calculationRowData, setCalculationRowData] =
    useState<CalculationRowData>(null);
  const [showCalculationDetailsModal, setShowCalculationDetailsModal] =
    useState(false);

  const requestBody = useGenerateQueryParams(name || "feeCalculation", {
    portfolio_id: portfolioId,
  });

  const { data, isFetching } = useGetFeeCalculationsQuery(requestBody);

  const [deleteFeeCalculation] = useDeleteFeeCalculationMutation();

  const handleConvertProforma = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<CalculationRowData>,
  ) => {
    event.stopPropagation();
    if (params.row.isHistoricallyMigrated) {
      toast.success(
        "This fee calculation cannot be viewed because it is historically migrated",
      );
    } else {
      setCalculationRowData(params.row);
      setOpenProformaModal(true);
    }
  };

  const handleDeleteCalculation = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<CalculationRowData>,
  ) => {
    event.stopPropagation();
    setDeleteCalculationId(params.row.id);
    setConfirmationModal(event.currentTarget);
  };

  const handleNavigate = (path: string) => navigate(path);

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio Name (${data?.totalCount || 0})`,
        "clientName",
        250,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontSize={14} fontWeight={500} color="var(--black)">
              {params?.row?.clientName}
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={500}
              color="var(--neutral-500)"
            >
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Calculation Date", "createdAt", 160, 1),
      type: "date",
      valueFormatter: (value) => matchDate(value.value),
    },
    { ...extraColumnsField("Fee Plan", "feePlanName", 180, 1) },
    {
      ...extraColumnsField("Fee Amount (₹)", "totalFee", 130, 1),
      valueGetter: (row) => row?.row?.totalFee,
      valueFormatter: (params) => moneyCommaSeparator(params.value) || 0,
      type: "number",
    },
    {
      ...extraColumnsField("Fee Date Range", "feeDateRange", 200, 1),
      valueGetter: (row) =>
        `${matchDate(row?.row?.startDate)} - ${matchDate(row?.row?.endDate)}`,
    },
    {
      ...extraColumnsField("Proforma Created", "isProforma", 150, 1),
      renderCell: (row) => (
        <StatusField statusString={row?.row?.isProforma.toString()} />
      ),
    },
    {
      ...extraColumnsField("Auto/Manual", "generatedType", 150, 1),
      valueGetter: (params) => handleCapitalize(params?.row?.generatedType),
    },
    {
      ...extraColumnsField("Actions", "action", 220, 1),
      headerAlign: "right",
      align: "right",
      type: "action",
      renderCell: (params) => {
        return (
          <Grid2 size={12} container justifyContent={"flex-end"} gap={1}>
            <GKButton
              disabled={params.row.isProforma}
              onClick={(event) => handleConvertProforma(event, params)}
            >
              Convert to Proforma
            </GKButton>

            <IconButton
              color="error"
              className="outline"
              onClick={(event) => handleDeleteCalculation(event, params)}
            >
              <DeleteIcon width={20} />
            </IconButton>
          </Grid2>
        );
      },
    },
  ];

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        maxHeight={"calc(100vh - 320px)"}
        totalCount={data?.totalCount || 0}
        name={name || "feeCalculation"}
        tableName={tableName || "fee_calculation"}
        headerComponent={
          <GKButton
            variant="contained"
            onClick={() => {
              handleNavigate(
                name
                  ? `/app/revenue/fee-calculation/${portfolioId}`
                  : `/app/revenue/fee-calculation/`,
              );
            }}
            startIcon={<PlusIcon width={18} />}
          >
            Fee Calculation
          </GKButton>
        }
        onRowClick={(params) => {
          if (params.row.isHistoricallyMigrated) {
            toast(() => (
              <span
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <ErrorfilledIcon
                  width={24}
                  color={paletteLightTheme.palette.warning.main}
                />
                <Typography>
                  This fee calculation cannot be viewed because it is
                  historically migrated
                </Typography>
              </span>
            ));
          } else {
            setCalculationRowData(params.row);
            setShowCalculationDetailsModal(true);
          }
        }}
        emptyComponent={
          <NoData
            text="Nothing Here Yet"
            subText="Save your first fee calculation to get started."
            customImg={
              <CalculatorIcon
                height={80}
                width={80}
                color="var(--neutral-100)"
              />
            }
            additionalComponent={
              <GKButton
                variant="contained"
                onClick={() =>
                  handleNavigate(
                    name
                      ? `/app/revenue/fee-calculation/${portfolioId}`
                      : `/app/revenue/fee-calculation/`,
                  )
                }
                startIcon={<PlusIcon width={18} />}
              >
                Fee Calculation
              </GKButton>
            }
          />
        }
      />

      <Show.When isTrue={showCalculationDetailsModal}>
        <CalculationDetailsModal
          open={showCalculationDetailsModal}
          setOpen={setShowCalculationDetailsModal}
          calculationRowData={calculationRowData}
        />
      </Show.When>

      <Show.When isTrue={openProformaModal}>
        <ProformaModal
          open={openProformaModal}
          setOpen={setOpenProformaModal}
          calculationRowData={calculationRowData}
        />
      </Show.When>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={async () => {
            try {
              await deleteFeeCalculation(deleteCalculationId).unwrap();
            } catch (err) {
              console.error("Delete fee calculation failed:", err);
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default FeeCalculation;
